/**
 * Card Data Mappers
 *
 * Pure functions that map API types (RuleComplianceAssessment + ComplianceRuleDocument)
 * to the presentation-layer AuditRuleCardData shape consumed by AuditRuleCard.
 */

import { CPT_CODES } from '@components/ui/forms/CPTCodePicker';
import type { ComplianceRuleDocument } from '@features/rules/types';

import type { AuditRuleCardData } from '../components/cards';
import type { CptAnalysisData } from '../hooks/useAuditIssues';
import type { BillingSupport, RuleComplianceAssessment } from '../types';

/**
 * Maps a rule compliance assessment + optional rule definition to the card's flat data shape.
 *
 * The rule definition provides enrichment (source info, facets, bodyMd) that isn't
 * available on the assessment alone. If missing, the card still renders — those
 * fields are all optional.
 */
export function mapAssessmentToCardData(
  assessment: RuleComplianceAssessment,
  ruleDefinition?: ComplianceRuleDocument | null,
): AuditRuleCardData {
  return {
    ruleId: assessment.rule_id,
    ruleTitle: assessment.rule_title || assessment.rule_id,
    severity: assessment.compliant === 'no' ? 'non_compliant' : 'warning',
    variant: 'rule',
    reason: assessment.reason,
    suggestion: assessment.suggestion ?? null,
    relevantSections: assessment.relevant_sections,

    // Enrichment from rule definition (optional)
    source: ruleDefinition?.source
      ? {
          documentTitle: ruleDefinition.source.document_title,
          documentDate: ruleDefinition.source.document_date,
          sourceUri: ruleDefinition.source.source_uri,
        }
      : null,
    bodyMd: ruleDefinition?.body_md ?? null,
    facets: ruleDefinition?.facets
      ? {
          cpt: ruleDefinition.facets.cpt,
          payer: ruleDefinition.facets.payer,
          providerType: ruleDefinition.facets.provider_type,
        }
      : null,

    // CPT fields — not applicable for rule variant
    suggestedCptCode: null,
    currentCptCode: null,
    cptDescription: null,
  };
}

/**
 * Maps billing support + CPT analysis data to a CPT variant card.
 * Returns null if there's no CPT mismatch to display.
 */
export function mapBillingToCardData(
  billingSupport: BillingSupport,
  cptAnalysis: CptAnalysisData,
): AuditRuleCardData | null {
  // No mismatch to show
  if (
    billingSupport.supports_suggested_cpt ||
    cptAnalysis.cptMatch ||
    !billingSupport.proposed_primary_code
  ) {
    return null;
  }

  const suggestedCode = billingSupport.proposed_primary_code;
  const currentCode = cptAnalysis.providerCptCodes?.[0] ?? null;
  const cptEntry = CPT_CODES.find(c => c.cpt === suggestedCode);

  return {
    ruleId: 'cpt-mismatch',
    ruleTitle: 'Update CPT code',
    severity: 'non_compliant',
    variant: 'cpt',
    reason: billingSupport.concerns.length > 0
      ? billingSupport.concerns.join('. ')
      : 'Documentation supports a different CPT code than the one currently billed.',
    suggestion: 'Update CPT code to reflect documented complexity.',
    relevantSections: null,
    source: null,
    bodyMd: null,
    facets: null,
    suggestedCptCode: suggestedCode,
    currentCptCode: currentCode,
    cptDescription: cptEntry?.description ?? null,
  };
}
